import type { FC } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  ReferenceArea,
} from 'recharts';
import { useChartZoom } from '@/components/charts/hooks/useChartZoom.ts';
import { LapBands } from '@/components/charts/LapBands.tsx';
import { LAP_STRIP_TICK_MARGIN } from '@/components/charts/types.ts';
import { chartHoverHandlers, hoverOnlyTooltip } from '@/components/charts/chartHover.ts';
import { chartTheme, formatChartTime } from '@/lib/colors/chartTheme.ts';
import { tokens } from '@pacevault/ui/lib/tokens.ts';
import type { ZoneScale } from '@/lib/colors/zoneColors.ts';
import { useZoneLineStroke } from '@/components/charts/hooks/useZoneLineStroke.tsx';
import { formatPaceTick } from '@/lib/format/formatters.ts';
import type { PacePoint } from '@/components/charts/chartData.ts';
import { m } from '@pacevault/i18n/messages.js';

interface PaceChartProps {
  data: PacePoint[];
  onActiveTimeChange?: (time: number | null) => void;
  onZoomComplete?: (from: string | number, to: string | number) => void;
  onSelectTime?: (time: number) => void;
  zoneScale?: ZoneScale;
}

export const PaceChart: FC<PaceChartProps> = (props) => {
  const zoom = useChartZoom({
    data: props.data,
    xKey: 'time',
    onZoomComplete: props.onZoomComplete,
    onClick: (x) => props.onSelectTime?.(Number(x)),
  });
  const zoneLine = useZoneLineStroke({
    scale: props.zoneScale,
    values: zoom.zoomedData.map((d) => d.pace),
    reversed: true,
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        syncId={'session-detail'}
        data={zoom.zoomedData}
        onMouseDown={zoom.onMouseDown}
        onMouseMove={(e) => {
          zoom.onMouseMove(e);
          if (props.onActiveTimeChange && e.activeLabel != null)
            props.onActiveTimeChange(Number(e.activeLabel));
        }}
        onMouseUp={zoom.onMouseUp}
        {...chartHoverHandlers(props.onActiveTimeChange)}
      >
        {zoneLine.defs}
        <XAxis
          dataKey="time"
          ticks={[
            zoom.zoomedData[0]?.time ?? 0,
            zoom.zoomedData[zoom.zoomedData.length - 1]?.time ?? 0,
          ]}
          tick={chartTheme.tick}
          tickLine={false}
          axisLine={chartTheme.axisLine}
          tickMargin={LAP_STRIP_TICK_MARGIN}
          tickFormatter={formatChartTime}
        />
        <YAxis
          domain={['auto', 'auto']}
          yAxisId="left"
          width={chartTheme.compactYAxisWidth}
          tick={chartTheme.tick}
          tickLine={false}
          axisLine={false}
          reversed
          tickCount={3}
          tickFormatter={formatPaceTick}
        />
        <RechartsTooltip {...hoverOnlyTooltip} />
        <LapBands rows={zoom.zoomedData} xKey="time" yAxisId="left" />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="pace"
          stroke={zoneLine.stroke ?? tokens.chartPace}
          strokeWidth={1.5}
          dot={false}
          name={m.ui_chart_series_pace()}
        />
        {zoom.refAreaLeft !== null && zoom.refAreaRight !== null && (
          <ReferenceArea
            yAxisId="left"
            x1={zoom.refAreaLeft}
            x2={zoom.refAreaRight}
            strokeOpacity={0.3}
            fill={tokens.accent}
            fillOpacity={0.15}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
